// Draft or improve a Gmail reply in the panel (W8, priority 4).
//
// The thread was read off Gmail on a click: the open message, or the selection when there is
// one, and whatever is already in the compose box. Scout drafts in the strategist's voice skill
// (`/api/extension/draft_reply`, up to 90 s). Insert hands the text to the side panel, which
// types it into the open compose box; Copy always works.

import { useEffect, useState } from 'react';
import { panelMessage, postExtensionDraftReply } from '../api/client';
import { useAuthContext } from '../auth/AuthContext';
import { insertResultFromEvent, postToExtension } from './bridge.ts';
import type { PageContext } from './bridge.ts';

export function ReplyTab({ context, onReread }: { context: PageContext | null; onReread: () => void }) {
  const { getToken } = useAuthContext();
  const [email, setEmail] = useState(context?.selection || context?.email_body || '');
  const [ownDraft, setOwnDraft] = useState(context?.compose_draft || '');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ line: string; tone: string } | null>(null);

  // A fresh read of the thread replaces what was there.
  useEffect(() => {
    setEmail(context?.selection || context?.email_body || '');
    setOwnDraft(context?.compose_draft || '');
  }, [context]);

  // The side panel answers an Insert with whether it found a compose box.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const result = insertResultFromEvent(event, window.parent);
      if (!result) return;
      setNote(result.ok ? { line: 'Inserted into the open reply.', tone: 'ok' }
        : { line: result.reason || 'No open reply box. Click Reply in Gmail, then Insert.', tone: 'warn' });
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  async function draft(improve: boolean) {
    setBusy(true); setNote(null);
    try {
      const sender = [context?.email_from_name, context?.email_from_address ? `<${context.email_from_address}>` : '']
        .filter(Boolean).join(' ');
      const answer = await postExtensionDraftReply(getToken, {
        selection: email, draft: improve ? ownDraft : undefined,
        subject: context?.email_subject, sender: sender || undefined,
      });
      if (!answer.available || !answer.draft) {
        setNote({ line: answer.unavailable_reason || 'Scout did not return a draft.', tone: 'bad' });
        return;
      }
      setReply(answer.draft);
    } catch (caught) {
      setNote({ line: panelMessage(caught, 'Draft failed.'), tone: 'bad' });
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(reply);
      setNote({ line: 'Copied.', tone: 'ok' });
    } catch {
      setNote({ line: 'Copy was blocked. Select the text and copy it.', tone: 'warn' });
    }
  }

  return (
    <div className="extsection">
      <div className="sblock">
        <h2 className="extcompany">{context?.email_subject || 'Gmail thread'}</h2>
        {context?.email_from_name || context?.email_from_address
          ? <p className="sidesub">From {context?.email_from_name} {context?.email_from_address ? `<${context.email_from_address}>` : ''}</p>
          : null}
        <div className="extactions">
          <button type="button" className="btn" onClick={onReread} disabled={busy}>Read the thread again</button>
        </div>
      </div>
      <div className="sblock">
        <label className="extfield">
          <span>Email to answer</span>
          <textarea rows={6} value={email} onChange={event => setEmail(event.target.value)} disabled={busy}
            placeholder="Open a thread and click Read the thread again, or highlight the message." />
        </label>
        <label className="extfield">
          <span>Your draft (optional)</span>
          <textarea rows={4} value={ownDraft} onChange={event => setOwnDraft(event.target.value)} disabled={busy}
            placeholder="Write or paste a draft to improve it." />
        </label>
        <div className="extactions">
          <button type="button" className="btn primary" disabled={busy || email.trim().length < 20}
            onClick={() => { void draft(false); }}>Draft reply</button>
          <button type="button" className="btn" disabled={busy || email.trim().length < 20 || !ownDraft.trim()}
            onClick={() => { void draft(true); }}>Improve my draft</button>
        </div>
        {busy ? <p className="extnote">Scout is drafting. This can take up to a minute and a half.</p> : null}
      </div>
      {reply ? (
        <div className="sblock">
          <label className="extfield">
            <span>Scout's draft</span>
            <textarea rows={10} value={reply} onChange={event => setReply(event.target.value)} />
          </label>
          <div className="extactions">
            <button type="button" className="btn primary" title="Nothing is sent. Insert types the draft into the open reply box."
              onClick={() => postToExtension({ type: 'scout:insert-reply', text: reply })}>
              Insert into Gmail
            </button>
            <button type="button" className="btn" onClick={() => { void copy(); }}>Copy</button>
            <span className="exthint">Nothing is sent</span>
          </div>
        </div>
      ) : null}
      {note ? <p className={'extnote ' + note.tone} role="status">{note.line}</p> : null}
    </div>
  );
}
